import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { 
  Users, Plus, Search, Filter, Mail, Phone, Building,
  Calendar, Tag, MoreVertical, ArrowUpRight, Star,
  X, Loader2, Upload, Edit2, Trash2, FileSpreadsheet, Download
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const leadStages = [
  { id: 'new', label: 'New', color: 'bg-blue-500' },
  { id: 'contacted', label: 'Contacted', color: 'bg-amber-500' },
  { id: 'qualified', label: 'Qualified', color: 'bg-violet-500' },
  { id: 'proposal', label: 'Proposal', color: 'bg-primary' },
  { id: 'negotiation', label: 'Negotiation', color: 'bg-pink-500' },
  { id: 'won', label: 'Won', color: 'bg-emerald-500' },
  { id: 'lost', label: 'Lost', color: 'bg-zinc-500' },
];

export default function LeadsCRM() {
  const { token } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStage, setFilterStage] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(null);
  const [editingLead, setEditingLead] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  const [newLead, setNewLead] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    stage: 'new',
    value: '',
    source: 'Marketing',
    tags: []
  });
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    fetchLeads();
  }, [token]);

  const fetchLeads = async () => {
    try {
      const response = await fetch(`${API}/api/marketing/leads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLeads(data.leads || []);
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setNewLead({
      name: '',
      email: '',
      phone: '',
      company: '',
      stage: 'new',
      value: '',
      source: 'Marketing',
      tags: []
    });
    setTagInput('');
    setEditingLead(null);
  };

  const handleCreateLead = async () => {
    if (!newLead.name || !newLead.email) {
      toast.error('Name and email are required');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API}/api/marketing/leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...newLead,
          value: parseFloat(newLead.value) || 0
        })
      });

      if (response.ok) {
        const data = await response.json();
        setLeads([data.lead, ...leads]);
        resetForm();
        setShowCreateModal(false);
        toast.success('Lead created successfully');
      } else {
        toast.error('Failed to create lead');
      }
    } catch (error) {
      toast.error('Error creating lead');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateLead = async () => {
    if (!newLead.name || !newLead.email) {
      toast.error('Name and email are required');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API}/api/marketing/leads/${editingLead.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...newLead,
          value: parseFloat(newLead.value) || 0
        })
      });

      if (response.ok) {
        setLeads(leads.map(l => l.id === editingLead.id ? { ...l, ...newLead, value: parseFloat(newLead.value) || 0 } : l));
        resetForm();
        setShowCreateModal(false);
        toast.success('Lead updated successfully');
      } else {
        toast.error('Failed to update lead');
      }
    } catch (error) {
      toast.error('Error updating lead');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm('Delete this lead?')) return;

    try {
      const response = await fetch(`${API}/api/marketing/leads/${leadId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        setLeads(leads.filter(l => l.id !== leadId));
        toast.success('Lead deleted');
      }
    } catch (error) {
      toast.error('Failed to delete lead');
    }
    setShowActionsMenu(null);
  };

  const handleEditLead = (lead) => {
    setEditingLead(lead);
    setNewLead({
      name: lead.name || '',
      email: lead.email || '',
      phone: lead.phone || '',
      company: lead.company || '',
      stage: lead.stage || 'new',
      value: lead.value?.toString() || '',
      source: lead.source || 'Marketing',
      tags: lead.tags || []
    });
    setShowCreateModal(true);
    setShowActionsMenu(null);
  };

  const updateLeadStage = async (leadId, newStage) => {
    try {
      const response = await fetch(`${API}/api/marketing/leads/${leadId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ stage: newStage })
      });
      if (response.ok) {
        setLeads(leads.map(lead => 
          lead.id === leadId ? { ...lead, stage: newStage } : lead
        ));
        toast.success('Lead stage updated');
      }
    } catch (error) {
      toast.error('Failed to update stage');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const text = event.target?.result;
        const lines = text.split('\n').filter(line => line.trim());
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        
        const leadsData = [];
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',');
          const lead = {};
          headers.forEach((header, idx) => {
            const mappedKey = {
              'name': 'name',
              'email': 'email',
              'phone': 'phone',
              'company': 'company',
              'value': 'value',
              'source': 'source',
              'stage': 'stage'
            }[header];
            if (mappedKey) {
              lead[mappedKey] = values[idx]?.trim() || '';
            }
          });
          if (lead.name || lead.email) {
            leadsData.push({
              ...lead,
              value: parseFloat(lead.value) || 0,
              stage: lead.stage || 'new'
            });
          }
        }

        if (leadsData.length === 0) {
          toast.error('No valid leads found in file');
          return;
        }

        setImporting(true);
        const response = await fetch(`${API}/api/marketing/leads/import`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ leads: leadsData })
        });

        if (response.ok) {
          const data = await response.json();
          toast.success(data.message || `${leadsData.length} leads imported`);
          fetchLeads();
          setShowImportModal(false);
        } else {
          toast.error('Failed to import leads');
        }
      } catch (error) {
        toast.error('Error parsing file');
      } finally {
        setImporting(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    };
    reader.readAsText(file);
  };

  const handleAddTag = (e) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      if (!newLead.tags.includes(tagInput.trim())) {
        setNewLead({ ...newLead, tags: [...newLead.tags, tagInput.trim()] });
      }
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setNewLead({ ...newLead, tags: newLead.tags.filter(t => t !== tagToRemove) });
  };

  const downloadTemplate = () => {
    const csvContent = "name,email,phone,company,value,source,stage\nJohn Doe,john@example.com,+1234567890,Acme Corp,5000,Website,new\nJane Smith,jane@example.com,+0987654321,Tech Inc,8000,Referral,contacted";
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'leads_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = 
      lead.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.company?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStage === 'all' || lead.stage === filterStage;
    return matchesSearch && matchesFilter;
  });

  const getStageInfo = (stageId) => {
    return leadStages.find(s => s.id === stageId) || leadStages[0];
  };

  const stats = {
    total: leads.length,
    newLeads: leads.filter(l => l.stage === 'new').length,
    qualified: leads.filter(l => l.stage === 'qualified' || l.stage === 'proposal').length,
    totalValue: leads.reduce((sum, l) => sum + (parseFloat(l.value) || 0), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="w-6 h-6 text-primary" />
            Leads & CRM
          </h1>
          <p className="text-muted-foreground">Manage your leads and customer relationships</p>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={() => setShowImportModal(true)}
            className="marketing-btn-secondary flex items-center gap-2"
            data-testid="import-leads-btn"
          >
            <Upload className="w-4 h-4" />
            <span>Bulk Import</span>
          </button>
          <button 
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
            className="marketing-btn-primary flex items-center gap-2"
            data-testid="add-lead-btn"
          >
            <Plus className="w-4 h-4" />
            <span>Add Lead</span>
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="metric-card">
          <p className="text-xs text-muted-foreground">Total Leads</p>
          <p className="text-2xl font-bold">{stats.total}</p>
        </div>
        <div className="metric-card">
          <p className="text-xs text-muted-foreground">New Leads</p>
          <p className="text-2xl font-bold text-blue-400">{stats.newLeads}</p>
        </div>
        <div className="metric-card">
          <p className="text-xs text-muted-foreground">Qualified</p>
          <p className="text-2xl font-bold text-violet-400">{stats.qualified}</p>
        </div>
        <div className="metric-card">
          <p className="text-xs text-muted-foreground">Pipeline Value</p>
          <p className="text-2xl font-bold text-primary">${(stats.totalValue / 1000).toFixed(0)}K</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search leads..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="marketing-input pl-10 w-full"
            data-testid="lead-search"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-2">
          <button
            onClick={() => setFilterStage('all')}
            className={`px-3 py-2 rounded-lg text-sm whitespace-nowrap transition-colors ${
              filterStage === 'all' 
                ? 'bg-primary text-primary-foreground' 
                : 'bg-secondary hover:bg-secondary/80'
            }`}
          >
            All
          </button>
          {leadStages.slice(0, 5).map(stage => (
            <button
              key={stage.id}
              onClick={() => setFilterStage(stage.id)}
              className={`px-3 py-2 rounded-lg text-sm whitespace-nowrap transition-colors ${
                filterStage === stage.id 
                  ? 'bg-primary text-primary-foreground' 
                  : 'bg-secondary hover:bg-secondary/80'
              }`}
            >
              {stage.label}
            </button>
          ))}
        </div>
      </div>

      {/* Leads List */}
      <div className="space-y-3">
        {filteredLeads.map(lead => {
          const stage = getStageInfo(lead.stage);
          return (
            <div key={lead.id} className="marketing-card hover:border-primary/30 transition-colors" data-testid={`lead-${lead.id}`}>
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                {/* Lead Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold shrink-0">
                      {lead.name?.charAt(0) || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold truncate">{lead.name}</h3>
                        {lead.score && (
                          <div className="flex items-center gap-1">
                            <Star className={`w-4 h-4 ${lead.score >= 80 ? 'text-amber-400 fill-amber-400' : 'text-muted-foreground'}`} />
                            <span className="text-xs text-muted-foreground">{lead.score}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Building className="w-3 h-3" />
                        <span className="truncate">{lead.company || 'No company'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="flex flex-wrap gap-4 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Mail className="w-4 h-4" />
                    <span className="truncate max-w-32">{lead.email}</span>
                  </div>
                  {lead.phone && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Phone className="w-4 h-4" />
                      <span>{lead.phone}</span>
                    </div>
                  )}
                </div>

                {/* Value & Stage */}
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-lg font-bold text-primary">${(lead.value || 0).toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">Deal Value</p>
                  </div>
                  
                  <select
                    value={lead.stage}
                    onChange={(e) => updateLeadStage(lead.id, e.target.value)}
                    className="marketing-input text-sm min-w-28"
                    data-testid={`stage-select-${lead.id}`}
                  >
                    {leadStages.map(s => (
                      <option key={s.id} value={s.id}>{s.label}</option>
                    ))}
                  </select>
                  
                  {/* Actions Menu */}
                  <div className="relative">
                    <button 
                      onClick={() => setShowActionsMenu(showActionsMenu === lead.id ? null : lead.id)}
                      className="p-2 hover:bg-secondary rounded-lg transition-colors"
                      data-testid={`lead-actions-${lead.id}`}
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>
                    {showActionsMenu === lead.id && (
                      <div className="absolute right-0 top-full mt-1 w-36 bg-card border border-border rounded-lg shadow-lg z-10">
                        <button 
                          onClick={() => handleEditLead(lead)}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-secondary transition-colors"
                          data-testid={`edit-lead-${lead.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                          Edit
                        </button>
                        <button 
                          onClick={() => handleDeleteLead(lead.id)}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-500/10 transition-colors"
                          data-testid={`delete-lead-${lead.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Tags & Meta */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-border/50">
                <div className="flex flex-wrap gap-2">
                  {(lead.tags || []).map((tag, i) => (
                    <span key={i} className="px-2 py-1 bg-secondary text-xs rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span>Source: {lead.source || 'Unknown'}</span>
                  {lead.created_at && (
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      Added: {new Date(lead.created_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {filteredLeads.length === 0 && (
          <div className="marketing-card text-center py-12">
            <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No leads found</p>
            <button 
              onClick={() => {
                resetForm();
                setShowCreateModal(true);
              }}
              className="marketing-btn-primary mt-4"
            >
              Add Your First Lead
            </button>
          </div>
        )}
      </div>

      {/* Create/Edit Lead Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h2 className="text-lg font-semibold">
                {editingLead ? 'Edit Lead' : 'Add New Lead'}
              </h2>
              <button 
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="p-1 hover:bg-secondary rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Name *</label>
                  <input
                    type="text"
                    value={newLead.name}
                    onChange={(e) => setNewLead({ ...newLead, name: e.target.value })}
                    className="marketing-input w-full"
                    placeholder="John Doe"
                    data-testid="lead-name-input"
                  />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Email *</label>
                  <input
                    type="email"
                    value={newLead.email}
                    onChange={(e) => setNewLead({ ...newLead, email: e.target.value })}
                    className="marketing-input w-full"
                    placeholder="john@example.com"
                    data-testid="lead-email-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Phone</label>
                  <input
                    type="tel"
                    value={newLead.phone}
                    onChange={(e) => setNewLead({ ...newLead, phone: e.target.value })}
                    className="marketing-input w-full"
                    placeholder="+1 234 567 890"
                  />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Company</label>
                  <input
                    type="text"
                    value={newLead.company}
                    onChange={(e) => setNewLead({ ...newLead, company: e.target.value })}
                    className="marketing-input w-full"
                    placeholder="Acme Corp"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Stage</label>
                  <select
                    value={newLead.stage}
                    onChange={(e) => setNewLead({ ...newLead, stage: e.target.value })}
                    className="marketing-input w-full"
                    data-testid="lead-stage-select"
                  >
                    {leadStages.map(s => (
                      <option key={s.id} value={s.id}>{s.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Deal Value ($)</label>
                  <input
                    type="number"
                    value={newLead.value}
                    onChange={(e) => setNewLead({ ...newLead, value: e.target.value })}
                    className="marketing-input w-full"
                    placeholder="5000"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Source</label>
                <select
                  value={newLead.source}
                  onChange={(e) => setNewLead({ ...newLead, source: e.target.value })}
                  className="marketing-input w-full"
                >
                  <option value="Marketing">Marketing</option>
                  <option value="Website">Website</option>
                  <option value="Referral">Referral</option>
                  <option value="Social Media">Social Media</option>
                  <option value="Cold Outreach">Cold Outreach</option>
                  <option value="Event">Event</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Tags</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {newLead.tags.map((tag, i) => (
                    <span 
                      key={i} 
                      className="px-2 py-1 bg-primary/20 text-primary text-xs rounded-full flex items-center gap-1"
                    >
                      {tag}
                      <button 
                        onClick={() => handleRemoveTag(tag)}
                        className="hover:text-red-400"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleAddTag}
                  className="marketing-input w-full"
                  placeholder="Type a tag and press Enter"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-4 border-t border-border">
              <button 
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="marketing-btn-secondary"
              >
                Cancel
              </button>
              <button 
                onClick={editingLead ? handleUpdateLead : handleCreateLead}
                disabled={saving}
                className="marketing-btn-primary flex items-center gap-2"
                data-testid="save-lead-btn"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editingLead ? 'Update Lead' : 'Create Lead'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h2 className="text-lg font-semibold">Bulk Import Leads</h2>
              <button 
                onClick={() => setShowImportModal(false)}
                className="p-1 hover:bg-secondary rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                <FileSpreadsheet className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-sm text-muted-foreground mb-4">
                  Upload a CSV file with columns: name, email, phone, company, value, source, stage
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="csv-upload"
                />
                <label 
                  htmlFor="csv-upload"
                  className="marketing-btn-primary cursor-pointer inline-flex items-center gap-2"
                >
                  {importing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Select CSV File
                    </>
                  )}
                </label>
              </div>
              <button 
                onClick={downloadTemplate}
                className="w-full marketing-btn-secondary flex items-center justify-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download Template
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
